/**
 * useRecordLinks — hooki F.RL (record linkages): powiązania między kartotekami
 * (kontrahent/pracownik/towar/dokument), polimorficzne, scoped per firma.
 *
 * Kontrakt backendu (patrz `apps/api/app/api/record-links/route.ts` + `[id]/route.ts` +
 * `[id]/restore/route.ts` + `apps/api/app/api/record-link-types/route.ts`):
 *  - GET  /record-links?entityType=&entityId=  → { groups: [...] } (pre-grouped server-side,
 *    `direction` liczony względem query'owanej encji; `X-Company-Id` zawęża do jednej firmy).
 *  - POST /record-links  → 201 { recordLink }. Wymaga JEDNEJ aktywnej firmy (400
 *    `COMPANY_CONTEXT_REQUIRED` w trybie "Wszystkie firmy").
 *  - PATCH /record-links/:id  { note }  → 200 { recordLink }.
 *  - DELETE /record-links/:id[?hard=true]  → 200 { deleted, hard }.
 *  - POST /record-links/:id/restore  → 200 { recordLink }.
 *  - GET /record-link-types?includeInactive=  → { recordLinkTypes: RecordLinkTypeDTO[] }
 *    (read-only tutaj — pełny CRUD słownika to osobny ekran ustawień).
 *
 * **`companyId` jest jawny, NIE polega na globalnym interceptorze** (`lib/api.ts`
 * dopina `X-Company-Id` z globalnej aktywnej firmy TYLKO gdy caller go jeszcze nie
 * ustawił). Blok powiązań ma WŁASNĄ firmę kontekstu (dla dokumentu — firma samego
 * dokumentu; dla kontrahenta/pracownika — aktywny scope), która może różnić się od
 * globalnej (np. dokument zapisany w innej firmie niż aktualny top-bar, albo top-bar
 * w trybie "Wszystkie firmy" podczas gdy dokument ma konkretną firmę). Każde wywołanie
 * (GET + wszystkie mutacje) dostaje `companyId` jako parametr i — gdy nie jest `null` —
 * ustawia jawny header `X-Company-Id`, który WYGRYWA z interceptorem (patrz `lib/api.ts`
 * — nie nadpisuje już ustawionego headera). Backend PATCH/DELETE/restore nie wymaga
 * headera wprost, ale `canManageEitherEnd` → `resolveRecordLinkOwner` dla końca typu
 * kontrahent CZYTA `X-Company-Id` z requestu (`resolveContractorRecordAccess`) — zły/
 * brakujący header może fałszywie odmówić dostępu (403) mimo poprawnych uprawnień.
 *
 * Query key: `['record-links', entityType, entityId, companyId]` — mutacje invalidują
 * z prefiksem `['record-links']` (partial match w React Query v5), żeby nie gubić
 * przypadku, gdy powiązanie dotyka encji spoza aktualnie zamontowanego bloku.
 */

import { useTranslation } from 'react-i18next';
import type {
  RecordLinkCardinality,
  RecordLinkOwnerType,
  RecordLinkSummary,
  RecordLinkTypeDTO,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface RecordLinkGroupItem {
  id: string;
  otherType: RecordLinkOwnerType;
  otherId: string;
  otherLabel: string | null;
  /** `documents.class` gdy `otherType==='document'` — pozwala zbudować trasę detalu
   * per klasa (kontrakt/faktura/pismo/…). `null`/pominięte dla pozostałych 3 typów. */
  otherClass?: string | null;
  /** Stały zestaw pól systemowych drugiego końca do panelu podglądu. `null` gdy oglądający
   * nie ma dostępu do drugiego końca (redakcja fail-closed razem z `otherLabel`/`otherClass`
   * po stronie API — patrz `apps/api/app/api/record-links/route.ts`). */
  summary?: RecordLinkSummary | null;
  note: string | null;
  createdAt: string;
}

export interface RecordLinkGroup {
  linkTypeId: string;
  direction: 'outgoing' | 'incoming';
  label: string;
  color: string | null;
  icon: string | null;
  items: RecordLinkGroupItem[];
}

interface RecordLinksResponse {
  groups: RecordLinkGroup[];
}

export interface RecordLinkDTOLike {
  id: string;
  companyId: string;
  linkTypeId: string;
  sourceType: RecordLinkOwnerType;
  sourceId: string;
  targetType: RecordLinkOwnerType;
  targetId: string;
  sourceLabel: string | null;
  targetLabel: string | null;
  note: string | null;
  createdAt: string;
}

interface RecordLinkTypesResponse {
  recordLinkTypes: RecordLinkTypeDTO[];
}

export type { RecordLinkCardinality, RecordLinkOwnerType, RecordLinkSummary, RecordLinkTypeDTO };

// ─── Query keys ─────────────────────────────────────────────────────────────

export function recordLinksKey(
  entityType: RecordLinkOwnerType,
  entityId: string | null | undefined,
  companyId: string | null | undefined,
) {
  return ['record-links', entityType, entityId ?? null, companyId ?? null] as const;
}

const RECORD_LINK_TYPES_KEY = ['record-link-types'] as const;

/**
 * Config axios z jawnym `X-Company-Id`, gdy `companyId` jest ustawiony — patrz
 * docstring nagłówkowy pliku. `undefined` (brak configu) gdy `companyId===null`,
 * bo w tym trybie ("Wszystkie firmy" dla kontrahenta/pracownika) nie chcemy
 * wymuszać headera — GET nadal działa (bez zawężenia do firmy), a mutacje i tak
 * są zablokowane w UI (`companyId===null` disable'uje "Dodaj powiązanie").
 */
function companyHeaderConfig(
  companyId: string | null,
): { headers: { 'X-Company-Id': string } } | undefined {
  return companyId ? { headers: { 'X-Company-Id': companyId } } : undefined;
}

// ─── Queries ────────────────────────────────────────────────────────────────

/** Lista powiązań (pre-grupowana server-side) — disabled dopóki `entityId` jest `null`. */
export function useRecordLinks(
  entityType: RecordLinkOwnerType,
  entityId: string | null | undefined,
  companyId: string | null,
) {
  return useQuery<RecordLinkGroup[]>({
    queryKey: recordLinksKey(entityType, entityId, companyId),
    queryFn: async () => {
      const res = await api.get<RecordLinksResponse>('/record-links', {
        params: { entityType, entityId },
        ...companyHeaderConfig(companyId),
      });
      return res.data.groups;
    },
    enabled: !!entityId,
    staleTime: 30_000,
  });
}

/** Słownik typów powiązań — read-only tutaj (picker "wybierz typ"); CRUD jest osobnym ekranem. */
export function useRecordLinkTypes(includeInactive = false) {
  return useQuery<RecordLinkTypeDTO[]>({
    queryKey: [...RECORD_LINK_TYPES_KEY, includeInactive],
    queryFn: async () => {
      const res = await api.get<RecordLinkTypesResponse>('/record-link-types', {
        params: includeInactive ? { includeInactive: 'true' } : undefined,
      });
      return res.data.recordLinkTypes;
    },
    staleTime: 2 * 60 * 1000,
  });
}

// ─── Error mapping (kod błędu API → klucz i18n) ────────────────────────────

function resolveRecordLinkErrorKey(code: string | undefined): string {
  switch (code) {
    case 'COMPANY_CONTEXT_REQUIRED':
      return 'tenant.recordLinks.errors.companyContextRequired';
    case 'INVALID_COMPANY_ID':
      return 'tenant.recordLinks.errors.invalidCompanyId';
    case 'SOURCE_TYPE_NOT_EDITABLE':
      return 'tenant.recordLinks.errors.sourceTypeNotEditable';
    case 'SELF_LINK_FORBIDDEN':
      return 'tenant.recordLinks.errors.selfLinkForbidden';
    case 'LINK_TYPE_NOT_FOUND':
      return 'tenant.recordLinks.errors.linkTypeNotFound';
    case 'TYPE_NOT_ALLOWED_FOR_ENTITY':
      return 'tenant.recordLinks.errors.typeNotAllowed';
    case 'SOURCE_COMPANY_MISMATCH':
    case 'TARGET_COMPANY_MISMATCH':
      return 'tenant.recordLinks.errors.companyMismatch';
    case 'CARDINALITY_VIOLATION':
      return 'tenant.recordLinks.errors.cardinalityViolation';
    case 'DUPLICATE_LINK':
      return 'tenant.recordLinks.errors.duplicateLink';
    case 'SOURCE_NOT_FOUND':
    case 'TARGET_NOT_FOUND':
    case 'ENTITY_NOT_FOUND':
    case 'LINK_NOT_FOUND':
      return 'tenant.recordLinks.errors.notFound';
    case 'PERMISSION_DENIED':
      return 'tenant.recordLinks.errors.permissionDenied';
    case 'HARD_DELETE_REQUIRES_SUPERUSER':
      return 'tenant.recordLinks.errors.hardDeleteRequiresSuperuser';
    case 'ALREADY_DELETED':
      return 'tenant.recordLinks.errors.alreadyDeleted';
    case 'NOT_DELETED':
      return 'tenant.recordLinks.errors.notDeleted';
    default:
      return 'tenant.recordLinks.errors.generic';
  }
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export interface CreateRecordLinkInput {
  sourceType: RecordLinkOwnerType;
  sourceId: string;
  targetType: RecordLinkOwnerType;
  targetId: string;
  linkTypeId: string;
  note?: string | null;
}

/**
 * Mutacje CRUD dla powiązań — invalidacja z prefiksem `['record-links']` (partial match,
 * React Query v5) zamiast dokładnego klucza per encja: tworzenie/kasowanie powiązania
 * dotyka DWÓCH encji (source + target), więc dokładny klucz zamontowanego bloku nie
 * wystarczy — inna karta/blok z drugą stroną powiązania też musi się odświeżyć.
 *
 * `companyId` — firma kontekstu bloku (patrz docstring nagłówkowy pliku), jawnie
 * dopięta jako `X-Company-Id` na KAŻDYM żądaniu (create/updateNote/delete/restore),
 * żeby operacja zawsze działała we WŁASNEJ firmie bloku, niezależnie od globalnej
 * aktywnej firmy w top-barze.
 *
 * Każda mutacja pokazuje toast (sukces/błąd) — kod błędu z API mapowany na czytelny
 * komunikat przez `resolveRecordLinkErrorKey` (patrz wyżej). `deleteLink` dodatkowo
 * oferuje "Cofnij" (akcja w toaście, patrz `ToastContext`) — woła `restoreLink` dla
 * właśnie odłączonego powiązania; sukces przywrócenia pokazuje własny toast
 * (`restoreSuccess`), więc nie duplikujemy osobnego komunikatu "cofnięto".
 */
export function useRecordLinkMutations(companyId: string | null) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();

  const headerConfig = companyHeaderConfig(companyId);

  function invalidateAll() {
    return queryClient.invalidateQueries({ queryKey: ['record-links'] });
  }

  const createLink = useMutation({
    mutationFn: async (input: CreateRecordLinkInput) => {
      const res = await api.post<{ recordLink: RecordLinkDTOLike }>(
        '/record-links',
        input,
        headerConfig,
      );
      return res.data.recordLink;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.recordLinks.toast.createSuccess') });
      void invalidateAll();
    },
    onError: (err: unknown) => {
      toast.show({
        variant: 'error',
        title: t(resolveRecordLinkErrorKey(extractErrorCode(err))),
      });
    },
  });

  const updateNote = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string | null }) => {
      const res = await api.patch<{ recordLink: RecordLinkDTOLike }>(
        `/record-links/${id}`,
        { note },
        headerConfig,
      );
      return res.data.recordLink;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.recordLinks.toast.updateSuccess') });
      void invalidateAll();
    },
    onError: (err: unknown) => {
      toast.show({
        variant: 'error',
        title: t(resolveRecordLinkErrorKey(extractErrorCode(err))),
      });
    },
  });

  const restoreLink = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ recordLink: RecordLinkDTOLike }>(
        `/record-links/${id}/restore`,
        undefined,
        headerConfig,
      );
      return res.data.recordLink;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.recordLinks.toast.restoreSuccess') });
      void invalidateAll();
    },
    onError: (err: unknown) => {
      toast.show({
        variant: 'error',
        title: t(resolveRecordLinkErrorKey(extractErrorCode(err))),
      });
    },
  });

  const deleteLink = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/record-links/${id}`, headerConfig);
    },
    onSuccess: (_data, id) => {
      toast.show({
        variant: 'success',
        title: t('tenant.recordLinks.toast.deleteSuccess'),
        duration: 8000,
        action: {
          label: t('tenant.recordLinks.undoUnlink'),
          onClick: () => {
            restoreLink.mutate(id);
          },
        },
      });
      void invalidateAll();
    },
    onError: (err: unknown) => {
      toast.show({
        variant: 'error',
        title: t(resolveRecordLinkErrorKey(extractErrorCode(err))),
      });
    },
  });

  return { createLink, updateNote, deleteLink, restoreLink };
}

// ─── Mutacje CRUD dla słownika typów powiązań (Settings → Typy powiązań) ───

/** Body współdzielone przez POST i PATCH `/record-link-types` (patrz route.ts). */
export interface RecordLinkTypeInputBase {
  forwardLabel: string;
  reverseLabel?: string | null;
  isSymmetric?: boolean;
  cardinality?: RecordLinkCardinality;
  color?: string | null;
  icon?: string | null;
  sourceTypes?: RecordLinkOwnerType[];
  targetTypes?: RecordLinkOwnerType[];
  isActive?: boolean;
  sortOrder?: number;
}

/** `code` obecny wyłącznie przy tworzeniu — PATCH nie przyjmuje zmiany (400 `CODE_IMMUTABLE`). */
export interface CreateRecordLinkTypeInput extends RecordLinkTypeInputBase {
  code: string;
}

export type UpdateRecordLinkTypeInput = RecordLinkTypeInputBase;

export interface SeedRecordLinkTypesResult {
  inserted: number;
  skipped: number;
}

/**
 * Mutacje zarządzania słownikiem typów powiązań — analogicznie do
 * `useDictionaryMutations` (useDictionary.ts): BEZ toastów/mapowania błędów
 * wbudowanego w hook — `RecordLinkTypesTab` woła `.mutate(input, {onSuccess,onError})`
 * per-callsite, bo różne błędy (CODE_TAKEN/CODE_IMMUTABLE/CANNOT_DELETE_SYSTEM_TYPE/
 * LINK_TYPE_IN_USE) wymagają różnej reakcji UI (toast + czasem błąd inline na polu).
 *
 * Invalidacja `RECORD_LINK_TYPES_KEY` jako PREFIKS (React Query v5 partial match)
 * odświeża oba warianty query (`includeInactive` true/false) jednym wywołaniem.
 */
export function useRecordLinkTypeMutations() {
  const queryClient = useQueryClient();

  function invalidateList() {
    return queryClient.invalidateQueries({ queryKey: RECORD_LINK_TYPES_KEY });
  }

  const createType = useMutation({
    mutationFn: async (values: CreateRecordLinkTypeInput) => {
      const res = await api.post<{ recordLinkType: RecordLinkTypeDTO }>(
        '/record-link-types',
        values,
      );
      return res.data.recordLinkType;
    },
    onSuccess: () => invalidateList(),
  });

  const updateType = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: UpdateRecordLinkTypeInput }) => {
      const res = await api.patch<{ recordLinkType: RecordLinkTypeDTO }>(
        `/record-link-types/${id}`,
        values,
      );
      return res.data.recordLinkType;
    },
    onSuccess: () => invalidateList(),
  });

  const deleteType = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/record-link-types/${id}`);
    },
    onSuccess: () => invalidateList(),
  });

  const seedDefaults = useMutation({
    mutationFn: async () => {
      const res = await api.post<SeedRecordLinkTypesResult>('/record-link-types/seed-defaults');
      return res.data;
    },
    onSuccess: () => invalidateList(),
  });

  return { createType, updateType, deleteType, seedDefaults };
}
